import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpGatewayService } from '../../global/services/http-gateway.service';
import {
  AccountContract,
  AccountMapContract,
  AccountsListDiffContract,
  AccountValidationContract,
  BaseAccountContract,
  CreateAccountContract,
  UpdateAccountContract,
} from '../store/accounts/types';
import { Priority } from '../store/segments/types';
import { PagedResult, PaginationOptions } from '../store/types';
import { createPaginationQueryParams } from '../utils/create-pagination-query-params';
import { HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AccountsApi {
  private httpService = inject(HttpGatewayService);

  public getAccounts(): Observable<AccountContract[]> {
    return this.httpService.get<AccountContract[]>('/accounts');
  }

  public createAccount(
    account: CreateAccountContract,
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<AccountContract> {
    return this.httpService.post<AccountContract>(
      `/accounts/${salesOrganizationId}/${clusterId}/single`,
      account,
    );
  }

  public getAccountsForCluster(
    salesOrganizationId: string,
    clusterId: string,
    paginationOptions: PaginationOptions,
  ): Observable<PagedResult<AccountContract>> {
    const queryParams = createPaginationQueryParams(paginationOptions);
    return this.httpService.get<PagedResult<AccountContract>>(
      `/clusters/${salesOrganizationId}/${clusterId}/accounts`,
      queryParams,
    );
  }

  public getAccountNumbersForCluster(
    salesOrganizationId: string,
    clusterId: string,
    paginationOptions: Pick<PaginationOptions, 'search' | 'sort'>,
  ): Observable<string[]> {
    const queryParams = createPaginationQueryParams(paginationOptions);
    return this.httpService.get<string[]>(
      `/clusters/${salesOrganizationId}/${clusterId}/accounts/account-numbers`,
      queryParams,
    );
  }

  public getAccountMapForCluster(
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<AccountMapContract[]> {
    return this.httpService.get<AccountMapContract[]>(
      `/sales-organizations/${salesOrganizationId}/clusters/${clusterId}/accounts/map`,
    );
  }

  public searchAccountsInCluster(
    salesOrganizationId: string,
    clusterId: string,
    searchString: string,
  ): Observable<AccountContract[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('search', searchString);
    return this.httpService.get<AccountContract[]>(
      `/accounts/${salesOrganizationId}/${clusterId}/search`,
      queryParams,
    );
  }

  public getAccount(accountId: string): Observable<AccountContract> {
    return this.httpService.get<AccountContract>(`/accounts/${accountId}`);
  }

  public getAccountsForSalesRep(): Observable<AccountContract[]> {
    return this.httpService.get<AccountContract[]>('/accounts');
  }

  public getAccountsForSalesRepInCluster(
    salesOrganizationId: string,
    clusterId: string,
    salesRepId?: string,
  ): Observable<AccountContract[]> {
    let queryParams = new HttpParams();
    if (salesRepId) {
      queryParams = queryParams.set('salesRepId', salesRepId);
    }
    return this.httpService.get<AccountContract[]>(
      `/accounts/${salesOrganizationId}/${clusterId}/sales-rep`,
      queryParams,
    );
  }

  public unsnoozeAccount(accountId: string): Observable<AccountContract> {
    return this.httpService.put<AccountContract>(
      `/accounts/${accountId}/unsnooze`,
      {},
    );
  }

  public getCountriesForCluster(
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<string[]> {
    return this.httpService.get<string[]>(
      `/clusters/${salesOrganizationId}/${clusterId}/countries`,
    );
  }

  public getTagsForCluster(
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<string[]> {
    return this.httpService.get<string[]>(
      `/accounts/${salesOrganizationId}/${clusterId}/tags`,
    );
  }

  public createAccounts(
    accounts: CreateAccountContract[],
    salesOrganizationId: string,
    clusterId: string,
    segmentsByPriority?: { [key in Priority]: string[] },
  ): Observable<AccountContract[]> {
    return this.httpService.post<AccountContract[]>(
      `/accounts/${salesOrganizationId}/${clusterId}`,
      {
        accounts,
        segmentsByPriority,
      },
    );
  }

  public validateAccounts(
    accounts: CreateAccountContract[],
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<AccountValidationContract> {
    return this.httpService.post<AccountValidationContract>(
      `/accounts/${salesOrganizationId}/${clusterId}/validate`,
      accounts,
    );
  }

  public getAccountsListDiff(
    accounts: CreateAccountContract[],
    salesOrganizationId: string,
    clusterId: string,
    segmentsByPriority?: { [key in Priority]: string[] },
  ): Observable<AccountsListDiffContract> {
    return this.httpService.post<AccountsListDiffContract>(
      `/accounts/${salesOrganizationId}/${clusterId}/diff`,
      {
        accounts,
        segmentsByPriority,
      },
    );
  }

  public updateAccounts(
    accounts: UpdateAccountContract[],
  ): Observable<AccountContract[]> {
    return this.httpService.put<AccountContract[]>('/accounts', accounts);
  }

  public searchAccountsForSalesRep(
    searchString: string,
  ): Observable<AccountContract[]> {
    let queryParams = new HttpParams().set('search', searchString);
    return this.httpService.get<AccountContract[]>(
      '/accounts/search',
      queryParams,
    );
  }

  public getAccountTagsAutocomplete(
    salesOrganizationId: string,
    clusterId: string,
    searchString: string,
  ): Observable<string[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('search', searchString);
    return this.httpService.get<string[]>(
      `/accounts/${salesOrganizationId}/${clusterId}/tags/search`,
      queryParams,
    );
  }

  public getAccountGroupsAutocomplete(
    salesOrganizationId: string,
    clusterId: string,
    searchString: string,
  ): Observable<string[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('search', searchString);
    return this.httpService.get<string[]>(
      `/accounts/${salesOrganizationId}/${clusterId}/groups/search`,
      queryParams,
    );
  }

  public getGroupsForCluster(
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<string[]> {
    return this.httpService.get<string[]>(
      `/accounts/${salesOrganizationId}/${clusterId}/groups`,
    );
  }

  public lookupByAccountNumbers(
    accountNumbers: string[],
  ): Observable<BaseAccountContract[]> {
    return this.httpService.post<BaseAccountContract[]>('/accounts/lookup', {
      accountNumbers,
    });
  }
}
